import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import isUserAgentMobileApp, {
  isUserAgentIphoneApp,
} from 'common/util/isUserAgentMobileApp';

import {
  ID_VERIFICATION_FLOW_RENTAL_APPLICATION,
} from 'common/idVerificationConstants';

import {
  SR_API_ACTION_USER_GENERATE_AUTO_LOGIN_URL,
  SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
} from 'api/actionTypes';

import {
  SR_WEB_APP_ACTION_TYPE_ID_VERIFICATION_RESULT,
} from 'client/components/SrMobileAppEventListener';

import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
  SR_MOBILE_APP_ACTION_TYPE_ID_VERIFICATION,
} from 'client/helpers/sendDataToMobileApp';

import log from 'client/lib/log';
import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';
import IdVerificationsActions from 'client/actions/IdVerifications';

import {
  BUTTON_THEME_BLUE_GREYCLIFF,
  BUTTON_THEME_GREY_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrIcon from 'client/components/SrIcon';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrSelfShowingVerificationModal from 'client/components/SrSelfShowingVerificationModal';

const debug = log.debug('sunroom:pages:SrIdVerification');

const SR_ID_VERIFICATION_STATE_VERIFY = 'SR_ID_VERIFICATION_STATE_VERIFY';
const SR_ID_VERIFICATION_STATE_VERIFY_IOS = 'SR_ID_VERIFICATION_STATE_VERIFY_IOS';
const SR_ID_VERIFICATION_STATE_VERIFY_ANDROID = 'SR_ID_VERIFICATION_STATE_VERIFY_ANDROID';
const SR_ID_VERIFICATION_STATE_IN_PROGRESS = 'SR_ID_VERIFICATION_STATE_IN_PROGRESS';
const SR_ID_VERIFICATION_STATE_ERROR = 'SR_ID_VERIFICATION_STATE_ERROR';

const SrIdVerification = ({
  listingId,
  userId,
  idVerificationFlow,
  onComplete,
  children,
}) => {
  const dispatch = useDispatch();
  const { userAgent, idVerification } = useBranch({
    userAgent: ['analytics', 'userAgent'],
    idVerification: ['idVerifications', 'byUserId', userId],
  });
  const [idVerificationStatus, setIdVerificationStatus] = useState(null);

  const onStartVerification = () => {
    if (!isUserAgentMobileApp(userAgent)) {
      setIdVerificationStatus(SR_ID_VERIFICATION_STATE_VERIFY);
      return;
    }

    if (isUserAgentIphoneApp(userAgent)) {
      setIdVerificationStatus(SR_ID_VERIFICATION_STATE_VERIFY_IOS);
      sendDataToMobileApp({
        actionType: SR_MOBILE_APP_ACTION_TYPE_ID_VERIFICATION,
      });
      return;
    }

    setIdVerificationStatus(SR_ID_VERIFICATION_STATE_VERIFY_ANDROID);
  };

  const setNullIdVerificationStatus = () => {
    setIdVerificationStatus(null);
  };

  const onSuccess = (idVerification) => {
    debug('onSuccess');
    setNullIdVerificationStatus();
    onComplete(idVerification);
  };

  const onFailure = (err) => {
    debug('onFailure', err);
    setIdVerificationStatus(SR_ID_VERIFICATION_STATE_ERROR);
  };

  const onClickAndroidMobileBrowser = () => {
    if (idVerificationFlow === ID_VERIFICATION_FLOW_RENTAL_APPLICATION) {
      dispatch(postWithState, {
        action: SR_API_ACTION_USER_GENERATE_AUTO_LOGIN_URL,
        payload: {
          redirectTo: `/rental-application/${userId}/${listingId}/form`,
        },
        onSuccess: (autoLoginUrl) => {
          sendDataToMobileApp({
            actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
            url: autoLoginUrl,
          });

          setNullIdVerificationStatus();
        },
      });
    }
  };

  const onSunroomMobileAppEvent = (evt) => {
    const { actionType, data } = evt.detail;

    if (actionType === SR_WEB_APP_ACTION_TYPE_ID_VERIFICATION_RESULT) {
      debug('onSunroomMobileAppEvent', data);
      if (data.success) {
        setIdVerificationStatus(SR_ID_VERIFICATION_STATE_IN_PROGRESS);

        dispatch(postWithState, {
          action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
          payload: {
            vouchedToken: data.vouchedJob.token,
            listingId,
          },
        }).then(() => {
          return dispatch(IdVerificationsActions.submit, {
            vouchedToken: data.vouchedJob.token,
            idVerificationFlow,
          });
        }).then((idVerification) => {
          onComplete(idVerification);
          setNullIdVerificationStatus();
        }).catch(() => {
          setNullIdVerificationStatus();
        });
      }
      else {
        setIdVerificationStatus(SR_ID_VERIFICATION_STATE_ERROR);
      }
    }
  };

  useEffect(() => {
    global.document.addEventListener('sunroomMobileAppEvent', onSunroomMobileAppEvent);

    return () => {
      global.document.removeEventListener('sunroomMobileAppEvent', onSunroomMobileAppEvent);
    };
  });

  return (
    <>
      {children({
        idVerification,
        onStartVerification,
        inProgress: idVerificationStatus === SR_ID_VERIFICATION_STATE_IN_PROGRESS,
        hasError: idVerificationStatus === SR_ID_VERIFICATION_STATE_ERROR,
      })}
      {idVerificationStatus === SR_ID_VERIFICATION_STATE_VERIFY && (
        <SrSelfShowingVerificationModal
          userId={userId}
          listingId={listingId}
          idVerificationFlow={idVerificationFlow}
          onSuccess={onSuccess}
          onFailure={onFailure}
          onClose={setNullIdVerificationStatus}
          updateIdVerification
        />
      )}
      {idVerificationStatus === SR_ID_VERIFICATION_STATE_VERIFY_ANDROID
      && idVerificationFlow === ID_VERIFICATION_FLOW_RENTAL_APPLICATION && (
        <SrSimpleMessageModal
          iconUrl='/images/verify-identity-blue.svg'
          headline='Verify Your Identity'
          underline
          message={
            <SrText size='18' lineHeight='30px'>
              {'For security purposes, we require renters to go through a quick '}
              {'verification before continuing.'}
            </SrText>
          }
          multiButtons={[
            {
              buttonText: (
                <SrFlex align='center' justify='center'>
                  <SrIcon className='external-link-white' marginRight='8px' />
                  Verify Identity
                </SrFlex>
              ),
              buttonTheme: BUTTON_THEME_BLUE_GREYCLIFF,
              onButtonClick: onClickAndroidMobileBrowser,
            },
            {
              buttonText: 'Cancel',
              buttonTheme: BUTTON_THEME_GREY_ADOBE,
              onButtonClick: setNullIdVerificationStatus,
            },
          ]}
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          maxWidth={375}
        />
      )}
    </>
  );
};

SrIdVerification.displayName = 'SrIdVerification';

SrIdVerification.propTypes = {
  listingId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  idVerificationFlow: PropTypes.string.isRequired,
  onComplete: PropTypes.func,
  children: PropTypes.node.isRequired,
};

SrIdVerification.defaultProps = {
  onComplete: () => {},
};

export default SrIdVerification;
